import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import SaveIcon from '@mui/icons-material/Save';
import { ReactComponent as UploadIcon } from 'assets/images/upload.svg';
import LinkIcon from '@mui/icons-material/Link';
import Dropzone, { FileRejection } from 'react-dropzone';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachmentList from './AttachmentList';
import { IEvidence, IEvidenceTracking, IVersion } from './ComplianceList';

interface EvidenceItemProps {
  evidence: IEvidence;
  version: IVersion;
  expandedEvidence: string;
  onToggleExpand: (erlNumber: string) => void;
  erlNumberToUpdate: string;
  evidenceTrackings: IEvidenceTracking[];
  isSelected: (evidence: IEvidence) => boolean;
  onTrackingChange: (
    status: boolean,
    evidence: IEvidence,
    notes?: string
  ) => void;
  savingNote: string;
  setSavingNote: (value: string) => void;
  getNoteValue: (erlNumber: string) => string;
  getInitialNote: (erlNumber: string) => string;
  onNoteChange: (erlNumber: string, value: string) => void;
  onCancelNote: (erlNumber: string) => void;
  isUploading: boolean;
  onFileSelected: (
    files: File[],
    rejected: FileRejection[],
    erlNumber: string
  ) => void;
  onExternalLinkOpen: (evidence: IEvidence) => void;
  onDeleteAttachment: (id: string, name: string) => void;
  onDownloadAttachment: (attachment: any) => void;
  canShowEvidence: (evidence: IEvidence, status: boolean[]) => boolean;
  status: boolean[];
}

const EvidenceItem: React.FC<EvidenceItemProps> = ({
  evidence,
  version,
  expandedEvidence,
  onToggleExpand,
  erlNumberToUpdate,
  evidenceTrackings,
  isSelected,
  onTrackingChange,
  savingNote,
  setSavingNote,
  getNoteValue,
  getInitialNote,
  onNoteChange,
  onCancelNote,
  isUploading,
  onFileSelected,
  onExternalLinkOpen,
  onDeleteAttachment,
  onDownloadAttachment,
  canShowEvidence,
  status,
}) => (
  <Box
    sx={{
      display: canShowEvidence(evidence, status) ? 'block' : 'none',
    }}
    className="app-box-border mb-2"
  >
    <Stack
      spacing={2}
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      className="cursor-pointer"
      onClick={() => onToggleExpand(evidence['ERL #'])}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        onClick={(e) => e.stopPropagation()}
      >
        {erlNumberToUpdate === evidence['ERL #'] ? (
          <CircularProgress className="mx-2" size={20} />
        ) : (
          <Checkbox
            checked={isSelected(evidence)}
            onChange={(event) => {
              onTrackingChange(event.target.checked, evidence);
            }}
          />
        )}
        <Stack alignItems="flex-start" spacing={2}>
          <Stack direction="row" spacing={2}>
            <Chip size="small" label={evidence['ERL #']} />
            {version.collectionType !== 'DB' && (
              <Typography>{evidence['Documentation Artifact']}</Typography>
            )}
          </Stack>
          <Typography className="mb-2">
            {evidence['Artifact Description']}
          </Typography>
          {version.collectionType !== 'DB' && (
            <Chip label={evidence['SCF Control Mappings']} size="small" />
          )}
        </Stack>
      </Stack>
      <Stack>
        {expandedEvidence === evidence['ERL #'] ? (
          <ExpandLessIcon />
        ) : (
          <ExpandMoreIcon />
        )}
      </Stack>
    </Stack>

    {expandedEvidence === evidence['ERL #'] && (
      <Collapse in={expandedEvidence === evidence['ERL #']}>
        <Box>
          <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />
          <Stack className="px-2">
            <Box sx={{ mb: 2 }}>
              <Typography className="fw-5" sx={{ mb: 1 }}>
                Description
              </Typography>
              <Typography color="textSecondary">
                {evidence['Artifact Description']}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ mb: 1 }}>Notes</Typography>
              <TextField
                fullWidth
                value={getNoteValue(evidence['ERL #'])}
                placeholder="Write a comment..."
                onChange={(e) =>
                  onNoteChange(evidence['ERL #'], e.target.value)
                }
                disabled={savingNote === evidence['ERL #']}
              />
              {getNoteValue(evidence['ERL #']) !==
                getInitialNote(evidence['ERL #']) && (
                <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
                  <AppPrimaryButtonOutlined
                    title="Cancel"
                    className="btn-small"
                    onClick={() => onCancelNote(evidence['ERL #'])}
                    disabled={savingNote === evidence['ERL #']}
                  />
                  <AppPrimaryButton
                    className="btn-small"
                    type="button"
                    startIcon={
                      savingNote === evidence['ERL #'] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    title={
                      savingNote === evidence['ERL #']
                        ? 'Saving...'
                        : 'Save notes'
                    }
                    onClick={() => {
                      setSavingNote(evidence['ERL #']);
                      onTrackingChange(
                        isSelected(evidence),
                        evidence,
                        getNoteValue(evidence['ERL #'])
                      );
                    }}
                    disabled={savingNote === evidence['ERL #']}
                  />
                </Stack>
              )}
            </Box>

            <Box>
              <Typography>Attachments</Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Dropzone
                  disabled={isUploading}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                      ['.docx'],
                    'application/vnd.ms-excel': ['.xls'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                    'text/csv': ['.csv'],
                    'image/*': [],
                    'video/*': [],
                    'text/plain': ['.txt'],
                  }}
                  maxFiles={5}
                  onDrop={(acceptedFiles, rejectedFiles) => {
                    onFileSelected(
                      acceptedFiles,
                      rejectedFiles,
                      evidence['ERL #']
                    );
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Box
                        sx={{
                          cursor: 'pointer',
                          border: '1px dashed lightgray',
                          padding: 5,
                        }}
                        mt={8}
                      >
                        {isUploading ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <CircularProgress size={20} className="mx-2" />
                            <Typography mt={2} color="text.secondary">
                              Uploading...
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <UploadIcon />
                            <Typography sx={{ color: '#7E7E7E' }} mt={8}>
                              Drag and drop your file here, or click to browse
                            </Typography>
                            <Typography sx={{ color: '#7E7E7E' }}>
                              PDF, Word, Excel, Images, Videos, or text files up
                              to 10MB
                            </Typography>
                          </>
                        )}
                      </Box>
                    </div>
                  )}
                </Dropzone>
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <AppPrimaryButtonOutlined
                title="Add external link"
                className="btn-small"
                startIcon={<LinkIcon htmlColor="#76A81F" />}
                onClick={() => onExternalLinkOpen(evidence)}
              />
              <AttachmentList
                evidenceTracking={evidenceTrackings.find(
                  (eT) => eT.erl_number === evidence['ERL #']
                )}
                onDelete={(attachmentId) => {
                  const attachment = evidenceTrackings
                    .find((eT) => eT.erl_number === evidence['ERL #'])
                    ?.attachments?.find((a) => a._id === attachmentId);
                  if (attachment) {
                    onDeleteAttachment(
                      attachmentId,
                      attachment.file_name || attachment.external_url
                    );
                  }
                }}
                onDownload={onDownloadAttachment}
              />
            </Box>
          </Stack>
        </Box>
      </Collapse>
    )}
  </Box>
);

export default EvidenceItem;
